import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getProducts, getCategories } from "../utils/api";
import ProductCard from "../components/ProductCard";
import "./CatalogPage.css";
import SEO from "../components/SEO";

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTypeId, setActiveTypeId] = useState(searchParams.get("typeId") || "");
  const [openCategoryId, setOpenCategoryId] = useState(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(searchParams.get("categoryId") || "");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const navigate = useNavigate();

  // Load products + categories on mount
  useEffect(() => {
    Promise.allSettled([getProducts(), getCategories()])
      .then(([p, c]) => {
        if (p.status === "fulfilled") setProducts(p.value.data.data || []);
        if (c.status === "fulfilled") setCategories(c.value.data.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Sync active category from URL params (e.g. from HomePage click)
  useEffect(() => {
    const catId = searchParams.get("categoryId") || "";
    setActiveCategoryId(catId);
  }, [searchParams]);

  // Filter + sort
  useEffect(() => {
    let result = [...products];

    if (activeCategoryId) {
      result = result.filter((p) => String(p.categoryId) === String(activeCategoryId));
    }

    if (activeTypeId) {
      result = result.filter((p) => String(p.productTypeId) === String(activeTypeId));
    }

    if (search.trim()) {
      const q = search.toLowerCase();

      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.description && p.description.toLowerCase().includes(q))
      );
    }

    if (sortBy === "price-asc") result.sort((a, b) => a.price - b.price);

    if (sortBy === "price-desc") result.sort((a, b) => b.price - a.price);

    if (sortBy === "newest") result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setFiltered(result);
  }, [products, activeCategoryId, activeTypeId, search, sortBy]);

  const handleCategoryClick = useCallback(
    (catId) => {
      setActiveCategoryId(catId);
      if (catId) {
        const cat = categories.find((c) => String(c.id) === String(catId));
        if (cat.name === "Індивідуальне замовлення") {
          navigate("/individual-order");
          return;
        }
        setSearchParams({ categoryId: catId, categoryName: cat?.name || "" });
      } else {
        setSearchParams({});
      }
    },
    [categories, setSearchParams, navigate]
  );

  const handleTypeClick = useCallback(
    (categoryId, typeId, typeName) => {
      setActiveCategoryId(categoryId);
      setActiveTypeId(typeId);

      setSearchParams({
        categoryId,
        typeId,
        typeName,
      });
    },
    [setSearchParams]
  );

  const activeCategoryName = searchParams.get("categoryName") || "";

  return (
    <div className="catalog-page page-enter">
      <SEO
        title="Каталог товарів"
        description="Каталог товарів Zori.place. Декор та фотошпалери ручної роботи в Україні."
        url="https://zori.place/catalog"
      />
      <div className="catalog-hero">
        <div className="container">
          <h1 className="section-title">
            {activeCategoryId && activeCategoryName ? activeCategoryName : "Каталог товарів"}
          </h1>
        </div>
      </div>

      <div className="container">
        <div className="catalog-layout">
          {/* SIDEBAR */}
          <aside className="catalog-sidebar">
            <button
              className={`sidebar-link ${!activeCategoryId ? "active" : ""}`}
              onClick={() => {
                setActiveCategoryId("");
                setActiveTypeId("");
                setSearchParams({});
              }}
            >
              Усі категорії
            </button>

            {categories.map((cat) => (
              <div key={cat.id} className="sidebar-category">
                <button
                  className={`sidebar-link ${
                    String(activeCategoryId) === String(cat.id) && !activeTypeId ? "active" : ""
                  }`}
                  onClick={() => {
                    handleCategoryClick(cat.id);
                    setOpenCategoryId((prev) => (prev === cat.id ? null : cat.id));
                  }}
                >
                  {cat.name}
                  {cat.productTypes?.length > 0 && (
                    <span className="sidebar-arrow">{openCategoryId === cat.id ? "▲" : "▼"}</span>
                  )}
                </button>

                {cat.productTypes?.length > 0 && (
                  <div
                    className={`sidebar-sublist ${
                      openCategoryId === cat.id ? "sidebar-sublist--open" : ""
                    }`}
                  >
                    {cat.productTypes.map((type) => (
                      <button
                        key={type.id}
                        className={`sidebar-sublink ${
                          String(activeTypeId) === String(type.id) ? "active" : ""
                        }`}
                        onClick={() => handleTypeClick(cat.id, type.id, type.name)}
                      >
                        ● {type.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </aside>

          {/* CONTENT */}
          <div className="catalog-content">
            {/* SEARCH + SORT */}
            <div className="catalog-filters">
              <div className="catalog-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>

                <input
                  type="text"
                  placeholder="Пошук товарів..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="catalog-sort">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="newest">Спочатку нові</option>
                  <option value="price-asc">Ціна: від низької</option>
                  <option value="price-desc">Ціна: від високої</option>
                </select>
              </div>
            </div>

            {/* ЕСЛИ КАТЕГОРИЯ НЕ ВЫБРАНА */}
            {!activeCategoryId ? (
              <div className="category-preview-grid">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="category-preview-card"
                    onClick={() => handleCategoryClick(cat.id)}
                  >
                    <img src={cat.image || "/placeholder.jpg"} alt={cat.name} />

                    <h3>{cat.name}</h3>
                  </div>
                ))}
              </div>
            ) : loading ? (
              <div className="spinner" />
            ) : filtered.length === 0 ? (
              <div className="catalog-empty">
                <span>🔍</span>
                <p>Товарів не знайдено.</p>
              </div>
            ) : (
              <>
                <p className="catalog-count">{filtered.length} товарів знайдено</p>

                <div className="catalog-grid">
                  {filtered.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
